import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { queryKeys, type Session } from '@/entities/session';

import type {
    GenerateSessionNoteInput,
    SessionNote,
    SetSessionNoteFeedbackInput,
    UpdateSessionNoteInput,
    WriteSessionNoteInput,
} from '../model/session-note.types';
import { sessionNotesApi } from './session-note.api';

export const mutationKeys = {
    all: ['session-notes'] as const,
    note: (sessionId: string) => [...mutationKeys.all, 'note', sessionId] as const,
    toggleCompletion: (sessionId: string) =>
        [...mutationKeys.note(sessionId), 'toggleCompletion'] as const,
    write: (sessionId: string) => [...mutationKeys.note(sessionId), 'write'] as const,
    generate: (sessionId: string) => [...mutationKeys.note(sessionId), 'generate'] as const,
    delete: (sessionId: string) => [...mutationKeys.note(sessionId), 'delete'] as const,
    setFeedback: (sessionId: string) => [...mutationKeys.note(sessionId), 'setFeedback'] as const,
    markAsOpened: (noteId: string) => [...mutationKeys.all, 'markAsOpened', noteId] as const,
};

export function useToggleCompletionOptions(sessionId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.toggleCompletion(sessionId),
        mutationFn: (command: UpdateSessionNoteInput) => sessionNotesApi.update(command),
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.detail(sessionId) });

            const previousSession = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            if (previousSession?.currentNote && variables.status) {
                queryClient.setQueryData<Session>(queryKeys.detail(sessionId), {
                    ...previousSession,
                    currentNote: {
                        ...previousSession.currentNote,
                        status: variables.status,
                        doneAt: variables.status === 'DONE' ? new Date() : null,
                    },
                });
            }

            return { previousSession };
        },
        onError: (error, _newData, context) => {
            if (context?.previousSession) {
                queryClient.setQueryData(queryKeys.detail(sessionId), context.previousSession);
            }
            console.error('Failed to toggle completion:', error);
            toast.error('Failed to update note status');
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.all });
        },
    });
}

export function useGenerateNoteOptions(sessionId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.generate(sessionId),
        mutationFn: (command: GenerateSessionNoteInput) => sessionNotesApi.generate(command),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: queryKeys.detail(sessionId) });

            const previousSession = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            return { previousSession };
        },
        onSuccess: (response, _, context) => {
            const { previousSession } = context;

            if (previousSession) {
                const currentNote = previousSession.currentNote || ({} as SessionNote);
                const currentGenerator =
                    currentNote.generator ||
                    ({ id: response.id } as unknown as SessionNote['generator']);
                queryClient.setQueryData<Session>(queryKeys.detail(sessionId), {
                    ...previousSession,
                    currentNote: {
                        ...currentNote,
                        status: 'TODO',
                        content: null,
                        generator: {
                            ...currentGenerator,
                            status: 'PREPARING_TRANSCRIPT',
                            errorType: null,
                        },
                    } as NonNullable<Session['currentNote']>,
                });
            }
        },
        onError: (error, _variables, context) => {
            if (context?.previousSession) {
                queryClient.setQueryData(queryKeys.detail(sessionId), context.previousSession);
            }
            console.error('Failed to generate note:', error);
            toast.error('Failed to generate note');
        },
    });
}

export function useWriteNoteOptions(sessionId: string) {
    const queryClient = useQueryClient();
    const queryKey = queryKeys.detail(sessionId);
    return mutationOptions({
        mutationKey: mutationKeys.write(sessionId),
        mutationFn: (command: WriteSessionNoteInput) => sessionNotesApi.write(command),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey });
            const previousSession = queryClient.getQueryData<Session>(queryKey);
            return { previousSession };
        },
        onSuccess: (serverResponse) => {
            const cached = queryClient.getQueryData<Session>(queryKey);
            if (!cached) return;

            const cachedContent = cached.currentNote?.content ?? serverResponse.content;
            queryClient.setQueryData<Session>(queryKey, {
                ...cached,
                currentNote: {
                    ...serverResponse,
                    content: cachedContent,
                } as NonNullable<Session['currentNote']>,
            });
        },
        onError: (error, _variables, context) => {
            if (context?.previousSession) {
                queryClient.setQueryData(queryKey, context.previousSession);
            }
            queryClient.invalidateQueries({ queryKey });
            console.error('Failed to save note:', error);
            toast.error('Failed to save note');
        },
    });
}

export function useDeleteNoteOptions(sessionId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.delete(sessionId),
        mutationFn: (noteId: string) => sessionNotesApi.delete(noteId),
        onMutate: async () => {
            await queryClient.cancelQueries({ queryKey: queryKeys.detail(sessionId) });

            const previousSession = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            if (previousSession?.currentNote) {
                const now = new Date();
                queryClient.setQueryData<Session>(queryKeys.detail(sessionId), {
                    ...previousSession,
                    currentNote: {
                        ...previousSession.currentNote,
                        content: null,
                        deletedAt: now,
                        updatedAt: now,
                    },
                });
            }

            return { previousSession };
        },
        onSuccess: () => {
            const cached = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            if (!cached?.currentNote?.generator) {
                return;
            }

            queryClient.setQueryData<Session>(queryKeys.detail(sessionId), {
                ...cached,
                generatedNoteDeletedAt: new Date().toISOString(),
            });
        },
        onError: (error, _noteId, context) => {
            if (context?.previousSession) {
                queryClient.setQueryData(queryKeys.detail(sessionId), context.previousSession);
            }
            console.error('Failed to delete note:', error);
            toast.error('Failed to delete note');
        },
    });
}

export function useSetFeedbackOptions(sessionId: string) {
    const queryClient = useQueryClient();
    return mutationOptions({
        mutationKey: mutationKeys.setFeedback(sessionId),
        mutationFn: (command: SetSessionNoteFeedbackInput) => sessionNotesApi.setFeedback(command),
        onMutate: async (variables) => {
            await queryClient.cancelQueries({ queryKey: queryKeys.detail(sessionId) });

            const previousSession = queryClient.getQueryData<Session>(queryKeys.detail(sessionId));

            if (previousSession?.currentNote?.generator) {
                queryClient.setQueryData<Session>(queryKeys.detail(sessionId), {
                    ...previousSession,
                    currentNote: {
                        ...previousSession.currentNote,
                        generator: {
                            ...previousSession.currentNote.generator,
                            feedback: variables.feedback,
                        },
                    },
                });
            }

            return { previousSession };
        },
        onError: (error, _variables, context) => {
            if (context?.previousSession) {
                queryClient.setQueryData(queryKeys.detail(sessionId), context.previousSession);
            }
            console.error('Failed to submit feedback:', error);
            toast.error('Failed to submit feedback');
        },
    });
}
